import React, { Fragment, useEffect, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import cn from 'classnames';

import { formatAmount } from '../../../../../utils/appointments/formatAmount';
import BackButton from '../../../../common/BackButton';
import Shape from '../../../../common/Shape';
import DiscountForm from '../DicountForm';
import Button from '../../../../Button';
import { enhance } from './enhance';
import Loader from '../../../../common/Loader';

const tScope = 'appointment.payment';

function AppointmentPaymentForm({
  couponPending,
  discount,
  couponErrors,
  calculateDiscount,
  startPayment,
  clear,
  onBack,
  formatMessage,
  paymentPending,
  couponVerification,
  couponVerificationPending,
  appointment: { copay, id },
}) {
  const loading = paymentPending || couponPending;

  const handlePayment = () => startPayment({ id });

  const isNotCouponAllowed =
    couponVerification &&
    (!couponVerification.couponsAvailable &&
      !couponVerification.automaticCouponAvailable);

  useEffect(() => clear, []);

  const freePayment = useMemo(
    () => discount && discount.discountAmountPercent === 100,
    [discount],
  );

  const { basePrice, discountPrice, totalPrice } = useMemo(() => {
    if (!discount) {
      return {
        basePrice: copay,
        discountPrice: formatAmount(0),
        totalPrice: copay,
      };
    }

    const { initialAmount, amount, discountAmountPercent } = discount;
    const discountAmount = formatAmount(initialAmount - amount);
    const roundDiscountPercent = Math.round(discountAmountPercent);

    return {
      basePrice: copay,
      discountPrice: `${discountAmount} (${roundDiscountPercent}%)`,
      totalPrice: amount,
    };
  }, [copay, discount]);

  const handleDiscountApply = coupon => {
    calculateDiscount({ coupon, appointmentId: id });
  };

  const paymentBtnText = freePayment
    ? 'common.next'
    : 'appointment.actions.payForVisit';

  if (couponVerificationPending) {
    return <Loader />;
  }

  return (
    <Fragment>
      {onBack && (
        <Shape styleName="PaymentHeader">
          <BackButton
            title={{ id: `${tScope}.title` }}
            minWidth={0}
            onBack={onBack}
          />
        </Shape>
      )}
      <Shape styleName="Payment">
        <div styleName="fadeout">
          <div
            styleName={
              isNotCouponAllowed ? 'PaymentContentFullWidth' : 'PaymentContent'
            }
          >
            {isNotCouponAllowed ? (
              ''
            ) : (
              <DiscountForm
                formatMessage={formatMessage}
                onApply={handleDiscountApply}
                serverErrors={couponErrors}
                loading={loading}
              />
            )}
            <div styleName="PaymentInfo">
              <div styleName="PaymentRow">
                <span>
                  {formatMessage({
                    id: isNotCouponAllowed
                      ? `${tScope}.copay`
                      : `${tScope}.basePrice`,
                  })}
                </span>
                <span>${formatAmount(basePrice)}</span>
              </div>
              {isNotCouponAllowed ? (
                ''
              ) : (
                <div styleName="PaymentRow">
                  <span>{formatMessage({ id: `${tScope}.discount` })}</span>
                  <span styleName={cn({ bold: !!discountPrice })}>
                    ${discountPrice}
                  </span>
                </div>
              )}
              <div styleName="PaymentSeparator" />
              <div styleName="PaymentRow blue bold">
                <span>{formatMessage({ id: `${tScope}.total` })}</span>
                <span>${formatAmount(totalPrice)}</span>
              </div>
            </div>
          </div>
          <div styleName="PaymentBtn">
            <Button
              onClick={handlePayment}
              disabled={loading}
              color="primary"
              raised
            >
              {formatMessage({ id: paymentBtnText })}
            </Button>
          </div>
        </div>
      </Shape>
    </Fragment>
  );
}

AppointmentPaymentForm.propTypes = {
  onBack: PropTypes.func,
  appointment: PropTypes.object,
  formatMessage: PropTypes.func,
  startPayment: PropTypes.func,
  couponPending: PropTypes.bool,
  paymentPending: PropTypes.bool,
  discount: PropTypes.object,
  couponErrors: PropTypes.object,
  clear: PropTypes.func,
  calculateDiscount: PropTypes.func,
  couponVerification: PropTypes.object,
  couponVerificationPending: PropTypes.bool,
};

export default enhance(AppointmentPaymentForm);



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/AppointmentDetailsPopup/AppointmentPaymentForm/index.jsx